/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  AgentDetailsCell,
  AgentNameCell,
  LastUpdatedCell,
  TestAgentButton,
  TableHeader,
  EmptyTableRow,
} from './components/ListAgents';
import {useGetAgents} from './hooks/useGetAgents';

import {PageContainer} from '../../components/PageContainer';
import {ROUTES} from '../../routes';
import {useUserStore} from '../../stores/userStore';

const AgentsListPage = () => {
  const navigate = useNavigate();
  const {fetch, agents, isLoading, error} = useGetAgents();
  const permissionsMap = useUserStore((state) => state.permissionsMap);

  useEffect(() => {
    void fetch();
  }, []);

  const handleCreateAgent = () => {
    navigate(ROUTES.AGENTS_CREATE);
  };

  const actions = (
    <Stack direction={'row'} spacing={2}>
      <Button
        variant={'contained'}
        onClick={handleCreateAgent}
        startIcon={<AddIcon />}
        disabled={!permissionsMap[PERMISSIONS.TRUSSTED_AGENT_CREATE]}
      >
        New Agent
      </Button>
    </Stack>
  );

  return (
    <PageContainer title={'Agents'} actions={actions} compact>
      {error ? (
        <Alert severity={'error'}>
          <AlertTitle>Error</AlertTitle>
          <Typography>{error}</Typography>
        </Alert>
      ) : isLoading ? (
        <Stack alignItems={'center'} spacing={2}>
          <CircularProgress />
          <Typography>Loading agents...</Typography>
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{width: '100%', overflow: 'auto'}}>
          <Table>
            <TableHeader />
            <TableBody>
              {agents.length === 0 ? (
                <EmptyTableRow
                  canCreateAgent={permissionsMap[PERMISSIONS.TRUSSTED_AGENT_CREATE]}
                  onCreateAgent={handleCreateAgent}
                />
              ) : (
                agents.map((agent) => (
                  <TableRow
                    key={agent.agentId}
                    hover
                    onClick={() => navigate(ROUTES.AGENTS_EDIT(agent.agentId))}
                    style={{cursor: 'pointer'}}
                  >
                    <TestAgentButton agentId={agent.agentId} agentName={agent.name} />
                    <TableCell>
                      <AgentNameCell name={agent.name} isDefault={agent.isDefault} greeting={agent.greeting} />
                    </TableCell>
                    <TableCell>
                      <AgentDetailsCell
                        abilities={agent.abilities || []}
                        faqs={agent.faqs || []}
                        documents={agent.documents}
                        phoneNumbers={agent.phoneNumbers}
                      />
                    </TableCell>
                    <TableCell>
                      <LastUpdatedCell updatedAt={agent.updatedAt} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export default AgentsListPage;
